import { selectedPageBorder, pdfViewer, pdfDocument, loadPdfPayload, pdfReady, setFindHighlights,
    toolbarHeight, viewerMode, updatePageDisplay, sidebarOpen, sidebarWidth } from './main';
import { PageInfo, ViewChanged, ResponseType, Zoom, NodeInfo, DeleteMark } from './shared/model/docavea-viewer-interface';
import { sendResponse } from './send-response';
// import { debug } from './debug';

let currentPage = 1;
let pageCheckerInterval: ReturnType<typeof setInterval> | undefined;
let firstLoad: boolean;
let selectionRectangles: any[];
let nodeInfos: NodeInfo[];
let selectionText: string;
let pageNumberBeforeRotate: number | undefined;
let pageBorderDiv: HTMLDivElement;
const splitMarkDivs: HTMLDivElement[] = [];
const deleteMarkDivs: HTMLDivElement[] = [];
let splitMarkNumbers: number[] = [];
let deleteMarks: DeleteMark[] = [];

// eslint-disable-next-line no-undef
let timeoutFunction: any;
let viewChangedUpdated = false;

export interface viewChangedOptions {
    selectionRectangles?: any[],
    selectionText?: string,
    pageNumberBeforeRotate?: number,
    nodeInfos?: NodeInfo[],
}

export async function viewChanged(_firstLoad: boolean, _viewerInitiatedPageChange: boolean, options?: viewChangedOptions) {
    // When scrolling the page using the WHEEL_EVENT message from the mothership the pdfViewer.currentPageNumber is not always correctly updated
    // at the time we read it. So we use the pageCheckerInterval to check if the page has changed and if so we call viewChanged again 
    // and get the current page number right.
    if (pageCheckerInterval === undefined) {
        pageCheckerInterval = setInterval(() => {
            if (pdfViewer && pdfDocument) {
                if (pdfViewer.currentPageNumber !== currentPage) {
                    viewChanged(false, false);
                }
            }
        }, 1000);
    }

    selectionText = options?.selectionText || '';
    selectionRectangles = [...options?.selectionRectangles || []];
    nodeInfos = [...options?.nodeInfos || []];
    // viewChanged calles may come at a high rate so some of them are not send. The following line preservs the pageNumberBeforeRotate until it has been used
    pageNumberBeforeRotate = pageNumberBeforeRotate || options?.pageNumberBeforeRotate;
    firstLoad = _firstLoad;

    viewChangedUpdated = true;
    if (timeoutFunction === undefined) {
        await _viewChanged(_viewerInitiatedPageChange);
        viewChangedUpdated = false;

        timeoutFunction = setTimeout(async () => {
            if (viewChangedUpdated === true) {
                viewChangedUpdated = false;
                await _viewChanged(_viewerInitiatedPageChange);
            }
            timeoutFunction = undefined;
        }, loadPdfPayload.throttleEventsTime);
    }
    setTimeout(() => {
        setFindHighlights();
    }, 100);
}

export const setSplitMarks = async (_splitMarkNumbers: number[]) => {
    splitMarkNumbers = _splitMarkNumbers;
    const pageInfos = await fillPageInfoArray();
    drawSplitMarks(pageInfos);
    // console.log('splitPageNumbers', splitMarkNumbers);
};

export const setDeleteMarks = async (_deleteMarks: DeleteMark[]) => {
    deleteMarks = _deleteMarks;
    const pageInfos = await fillPageInfoArray();
    drawDeleteMarks(pageInfos);
    // console.log('splitPageNumbers', splitMarkNumbers);
};

export const clearDivs = () => {
    clearPageBorder();
    clearSplitMarks();
};

async function _viewChanged(viewerInitiatedPageChange: boolean) {
    // pdfDocument might be null in case RequestType.SET_ORIENTATIONS is called, because the file is then reloaded
    if (pdfDocument === null) {
        return;
    }

    // The await pdfReady is needed for the data to be reported correctly
    // It does in the current pdfjs version reset the viewer currentPage to 1 on firstLoad
    // therefor we set currentPage back to loadPdfPayload.pageNumber on firstLoad
    if (await pdfReady() < 0) {
        return;
    }

    if (firstLoad) {
        currentPage = loadPdfPayload.pageNumber;
        pdfViewer.currentPageNumber = currentPage;
    } else {
        currentPage = pageNumberBeforeRotate || pdfViewer.currentPageNumber;
        pageNumberBeforeRotate = undefined;
    }

    // Update page navigation display in viewerTab mode
    if (viewerMode === 'viewerTab') {
        updatePageDisplay();
    }

    const pageInfos = await fillPageInfoArray();

    const viewerContainer = document.getElementById('viewerContainer');
    if (viewerContainer) {
        if (pageInfos) {
            borderSelectedPage(pageInfos);
            drawSplitMarks(pageInfos);
            // enable test rectangles by writing localStorage.drawRectangles=true in the browser console
            // if (localStorage.getItem('drawRectangles') === 'true') {
            //     drawPageTestRects(pageInfos);
            // }

            // send response to host
            const scale = loadPdfPayload.scalePayload.zoom === Zoom.SCALE ? +pdfViewer.currentScaleValue : 1;
            const payload: ViewChanged = {
                firstLoad,
                currentPage,
                numberOfPages: pdfDocument.numPages,
                pageInfos,
                selectionRectangles,
                nodeInfos,
                selectionText,
                clientHeight: viewerContainer.clientHeight,
                clientWidth: viewerContainer.clientWidth,
                centerX: viewerContainer.scrollLeft + viewerContainer.clientWidth / 2,
                centerY: viewerContainer.scrollTop + viewerContainer.clientHeight / 2,
                scalePayload: {
                    scale,
                    zoom: loadPdfPayload.scalePayload.zoom,
                    dualPage: loadPdfPayload.scalePayload.dualPage,
                },
                orientations: loadPdfPayload.orientations,
                viewerInitiatedPageChange,
            };

            sendResponse(ResponseType.VIEW_CHANGED, payload);
        }
    } else {
        console.error(`getElementById('viewerContainer') failed`);
    }
}

function fillPageInfoArray() {
    const pageInfos: PageInfo[] = [];
    const searchFor = 12;
    const start = Math.max(currentPage - searchFor, 1);
    const end = Math.min(currentPage + searchFor, pdfDocument.numPages);
    let first = -1;

    const viewerMainEl = document.getElementById('viewer');
    if (viewerMainEl) {
        const multiPage = pdfViewer._scrollMode !== 3;  // 3 = ScrollMode.PAGE (forced by pdfjs at >10,000 pages)
        if (multiPage) {
            for (let pageNumber = start; pageNumber <= end; pageNumber++) {
                const pageContainerEl = viewerMainEl.querySelector(`div[data-page-number='${pageNumber}']`);
                if (pageContainerEl) {
                    const rect = pageContainerEl.getBoundingClientRect();
                    const pageInfo: PageInfo = {
                        pageNumber,
                        scale: pdfViewer._pages[pageNumber - 1].scale,
                        orientation: pdfViewer._pages[pageNumber - 1].rotation,
                        top: rect.top,
                        left: rect.left,
                        width: rect.width,
                        height: rect.height,
                    };

                    pageInfos.push(pageInfo);
                    if (isVisible(rect)) {
                        if (first === -1) {
                            first = Math.max(0, pageInfos.length - 2);
                        }
                    } else {
                        if (first >= 0) {
                            break;
                        }
                    }
                } else {
                    console.warn(`querySelector(div[data-page-number='${pageNumber}']) not found`);
                }
            }
        } else {
            const pageContainerEl = document.getElementsByClassName('page'); // single page
            if (pageContainerEl) {
                first = 0;
                const rect = pageContainerEl[0].getBoundingClientRect();
                const pageNumber = currentPage;
                const pageInfo: PageInfo = {
                    pageNumber,
                    scale: pdfViewer._pages[pageNumber - 1].scale,
                    orientation: pdfViewer._pages[pageNumber - 1].rotation,
                    top: rect.top,
                    left: rect.left,
                    width: rect.width,
                    height: rect.height,
                };

                pageInfos.push(pageInfo);
            } else {
                console.error(`fillPageInfoArray(): document.getElementsByClassName('page') failed`);
            }
        }
    } else {
        console.error(`getElementById('viewer') failed`);
    }

    if (first === -1) {
        console.error('fillPageInfoArray() failed to find first visible page');
        first = 0;
    }

    return pageInfos.slice(first);
}

function isVisible(rect: ClientRect) {
    return (
        rect.bottom > 0 &&
        rect.right > 0 &&
        rect.left <= (window.innerWidth || document.documentElement.clientWidth) &&
        rect.top <= (window.innerHeight || document.documentElement.clientHeight)
    );
}

const borderSelectedPage = (pageInfos: PageInfo[]) => {
    clearPageBorder();
    const pageBorderWidth = 3;
    for (const pageInfo of pageInfos) {
        if (pageInfo.pageNumber === currentPage) {
            const container = document.getElementById('viewerContainer');
            pageBorderDiv = document.createElement('div');
            if (pageBorderDiv && container) {
                // Account for sidebar offset in viewerTab mode (use dynamic sidebarWidth)
                const sidebarOffset = (viewerMode === 'viewerTab' && sidebarOpen) ? sidebarWidth : 0;

                pageBorderDiv.style.width = `${pageInfo.width - 2 * pageBorderWidth}px`;
                pageBorderDiv.style.height = `${pageInfo.height - 2 * pageBorderWidth}px`;
                pageBorderDiv.style.top = `${pageInfo.top + container.scrollTop - toolbarHeight}px`;
                pageBorderDiv.style.left = `${pageInfo.left + container.scrollLeft - sidebarOffset}px`;
                pageBorderDiv.style.borderColor = selectedPageBorder;
                pageBorderDiv.style.position = 'absolute';
                pageBorderDiv.style.borderStyle = 'solid';
                pageBorderDiv.style.borderWidth = `${pageBorderWidth}px`;
                pageBorderDiv.style.margin = '0px';
                pageBorderDiv.style.pointerEvents = 'none';
                container.appendChild(pageBorderDiv);
            } else {
                console.error(`borderSelectedPage(): getElementById('viewerContainer') or createElement('div') failed`);
            }
        }
    }
};

export const drawSplitMarks = (pageInfos: PageInfo[]) => {
    clearSplitMarks();
    const splitMarkWidth = 5;
    for (const pageInfo of pageInfos) {
        if (splitMarkNumbers.includes(pageInfo.pageNumber)) {
            const container = document.getElementById('viewerContainer');
            const splitMarkDiv = document.createElement('div');
            if (splitMarkDiv && container) {
                // Account for sidebar offset in viewerTab mode (use dynamic sidebarWidth)
                const sidebarOffset = (viewerMode === 'viewerTab' && sidebarOpen) ? sidebarWidth : 0;

                splitMarkDiv.style.width = `${pageInfo.width - 3}px`;
                splitMarkDiv.style.height = `${2 * pageInfo.height / 3}px`;
                splitMarkDiv.style.top = `${pageInfo.top + container.scrollTop - 6}px`;
                splitMarkDiv.style.left = `${pageInfo.left + container.scrollLeft - sidebarOffset - 3}px`;
                splitMarkDiv.style.borderColor = '#FF7F7F';
                splitMarkDiv.style.position = 'absolute';
                splitMarkDiv.style.borderStyle = 'dotted';
                splitMarkDiv.style.borderWidth = `${splitMarkWidth}px`;
                splitMarkDiv.style.borderBottom = 'none';
                splitMarkDiv.style.margin = '0px';
                splitMarkDiv.style.pointerEvents = 'none';
                container.appendChild(splitMarkDiv);
                splitMarkDivs.push(splitMarkDiv);
            } else {
                console.error(`drawSplitMarks(): getElementById('viewerContainer') or createElement('div') failed`);
            }
        }
    }
};

export const drawDeleteMarks = (pageInfos: PageInfo[]) => {
    clearDeleteMarks();
    for (const pageInfo of pageInfos) {
        const isPageNumberDeleted = deleteMarks.some((mark) =>
            pageInfo.pageNumber >= mark.start && pageInfo.pageNumber <= mark.end
        );
        if (isPageNumberDeleted) {
            const container = document.getElementById('viewerContainer');
            const deleteMarkDiv = document.createElement('div');
            if (deleteMarkDiv && container) {
                // Account for sidebar offset in viewerTab mode (use dynamic sidebarWidth)
                const sidebarOffset = (viewerMode === 'viewerTab' && sidebarOpen) ? sidebarWidth : 0;

                deleteMarkDiv.style.width = `${pageInfo.width}px`;
                deleteMarkDiv.style.height = `${pageInfo.height + 6}px`;
                deleteMarkDiv.style.top = `${pageInfo.top + container.scrollTop - 3}px`;
                deleteMarkDiv.style.left = `${pageInfo.left + container.scrollLeft - sidebarOffset}px`;
                deleteMarkDiv.style.position = 'absolute';
                deleteMarkDiv.style.margin = '0px';
                deleteMarkDiv.style.pointerEvents = 'none';
                deleteMarkDiv.style.backgroundColor = 'gray';
                deleteMarkDiv.style.opacity = '0.5';
                container.appendChild(deleteMarkDiv);
                deleteMarkDivs.push(deleteMarkDiv);
            } else {
                console.error(`drawDeleteMarks(): getElementById('viewerContainer') or createElement('div') failed`);
            }
        }
    }
};

const clearPageBorder = () => {
    if (pageBorderDiv !== undefined) {
        pageBorderDiv.remove();
    }
};

const clearSplitMarks = () => {
    for (const splitMarkDiv of splitMarkDivs) {
        splitMarkDiv.remove();
    }
};

const clearDeleteMarks = () => {
    for (const deleteMarkDiv of deleteMarkDivs) {
        deleteMarkDiv.remove();
    }
};

// Test code to draw boxes around pages
// let divs: HTMLDivElement[] = [];
// function drawPageTestRects(pageInfos: PageInfo[]) {
//     for (const div of divs) {
//         div.remove();
//     }
//     divs = [];
//     for (const pageInfo of pageInfos) {
//         const container = document.getElementById('viewerContainer');
//         const div = document.createElement('div');
//         if (div && container) {
//             divs.push(pageBorderDiv);
//             div.style.width = `${pageInfo.width}px`;
//             div.style.height = `${pageInfo.height}px`;
//             div.style.top = `${pageInfo.top + container.scrollTop}px`;
//             div.style.left = `${pageInfo.left + container.scrollLeft}px`;
//             div.style.borderColor = pageInfo.pageNumber === currentPage ? 'red' : 'green';
//             div.style.position = 'absolute';
//             div.style.borderStyle = 'solid';
//             div.style.borderWidth = '3px';
//             div.style.margin = '-3px';
//             div.style.pointerEvents = 'none';
//             container.appendChild(div);
//         } else {
//             console.error(`getElementById('viewerContainer') or createElement('div') failed`);
//         }
//     }
// }
